import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import styled, { css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import useHistory from "routing/useHistory";
import NumberUtils from "util/NumberUtils";
import { Col, Row } from "components/bootstrap";
import { FormikInput, FormSubmit, Section, Spinner } from "components/common";
import HideOnCloud from "util/conditional/HideOnCloud";
import HiddenFieldWrapper from "components/indices/IndexSetConfigurationForm/HiddenFieldWrapper";
import { parseFieldRestrictions } from "components/indices/helpers/fieldRestrictions";
import useArchiveIndexSetConfiguration from "archive/hooks/useArchiveIndexSetConfiguration";
import usePermissions from "hooks/usePermissions";
import useArchiveIndexSetConfigurationMutation from "archive/hooks/useArchiveIndexSetConfigurationMutation";
import ArchiveRoutes from "archive/Routes";
const FlexWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
  `
);
const SubmitWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const validateForm = (values) => {
  const errors = {};
  const tValidation = (key) => i18n.t(`archive_index_set_configuration_form.validation.${key}`, { ns: "enterprise_archive" });
  if (!(values.shards > 0)) {
    errors.shards = tValidation("min_shard");
  }
  if (!(values.replicas >= 0)) {
    errors.replicas = tValidation("cannot_be_negative");
  }
  if (values.automated_shard_management_enabled && !(values.shard_size_bytes > 0)) {
    errors.shard_size_bytes = tValidation("cannot_be_negative");
  }
  const requiredFields = ["shards", "replicas"];
  requiredFields.forEach((field) => {
    if (values[field] === "" || typeof values[field] === "undefined") {
      errors[field] = tValidation("required");
    }
  });
  return errors;
};
const RestoredIndexSetConfigurationForm = () => {
  const { t } = useTranslation("enterprise_archive");
  const { push } = useHistory();
  const { data: indexSetData, isLoading: isLoadingIndexSet } = useArchiveIndexSetConfiguration();
  const { updateArchiveIndexSetConfigurationMutation } = useArchiveIndexSetConfigurationMutation();
  const { isPermitted } = usePermissions();
  const ignoreFieldRestrictions = isPermitted("indexsets_field_restrictions:edit");
  const initialValues = useMemo(() => {
    if (!indexSetData)
      return {
        shards: void 0,
        replicas: void 0,
        automated_shard_management_enabled: void 0,
        shard_size_bytes: void 0,
        field_restrictions: void 0
      };
    const { shards, replicas, automated_shard_management_enabled, shard_size_bytes, field_restrictions } = indexSetData;
    return {
      shards,
      replicas,
      automated_shard_management_enabled,
      shard_size_bytes,
      field_restrictions
    };
  }, [indexSetData]);
  if (isLoadingIndexSet) return /* @__PURE__ */ React.createElement(Spinner, null);
  if (!indexSetData) return null;
  const { immutableFields, hiddenFields } = parseFieldRestrictions(indexSetData.field_restrictions);
  const handleAutomatedShardManagementChange = (value, setFieldValue, setFieldTouched) => {
    setFieldValue("automated_shard_management_enabled", !value);
    if (!value === false) {
      setFieldValue("shard_size_bytes", indexSetData.shard_size_bytes);
      setFieldTouched("shard_size_bytes");
    }
  };
  const handleSubmit = (values) => {
    updateArchiveIndexSetConfigurationMutation(values).then(() => {
      push(ArchiveRoutes.INDEX_SET.SHOW);
    });
  };
  const handleCancel = () => {
    push(ArchiveRoutes.INDEX_SET.SHOW);
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      validateOnMount: true,
      onSubmit: handleSubmit,
      enableReinitialize: true,
      validate: validateForm,
      initialValues
    },
    ({ isValid, isValidating, isSubmitting, values, setFieldValue, setFieldTouched }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(FlexWrapper, null, /* @__PURE__ */ React.createElement(Section, { title: t("archive_index_set_configuration_form.details") }, /* @__PURE__ */ React.createElement(HideOnCloud, null, /* @__PURE__ */ React.createElement(HiddenFieldWrapper, { hiddenFields, ignoreFieldRestrictions }, /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "shards",
        label: t("archive_index_set_configuration_form.index_shards_label"),
        name: "shards",
        help: /* @__PURE__ */ React.createElement(
          Trans,
          {
            t,
            i18nKey: "archive_index_set_configuration_form.index_shards_help",
            components: { lineBreak: /* @__PURE__ */ React.createElement("br", null) }
          }
        ),
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("shards")) && !ignoreFieldRestrictions
      }
    ), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "replicas",
        label: t("archive_index_set_configuration_form.index_replica_label"),
        name: "replicas",
        help: t("archive_index_set_configuration_form.index_replica_help"),
        required: true,
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("replicas")) && !ignoreFieldRestrictions
      }
    ), /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "checkbox",
        id: "automated-shard-management-enabled",
        label: t("archive_index_set_configuration_form.automated_shard_management_label"),
        name: "automated_shard_management_enabled",
        help: t("archive_index_set_configuration_form.automated_shard_management_help"),
        onChange: () => handleAutomatedShardManagementChange(
          values.automated_shard_management_enabled,
          setFieldValue,
          setFieldTouched
        ),
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("automated_shard_management_enabled")) && !ignoreFieldRestrictions
      }
    ), values.automated_shard_management_enabled && /* @__PURE__ */ React.createElement(
      FormikInput,
      {
        type: "number",
        id: "shard-size-bytes",
        label: t("archive_index_set_configuration_form.shard_size_label"),
        name: "shard_size_bytes",
        help: /* @__PURE__ */ React.createElement(
          Trans,
          {
            t,
            i18nKey: "archive_index_set_configuration_form.shard_size_help",
            components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
          }
        ),
        required: true,
        addonAfter: NumberUtils.formatBytes(values.shard_size_bytes),
        disabled: (immutableFields == null ? void 0 : immutableFields.includes("shard_size_bytes")) && !ignoreFieldRestrictions
      }
    )))), /* @__PURE__ */ React.createElement(SubmitWrapper, null, /* @__PURE__ */ React.createElement(
      FormSubmit,
      {
        disabledSubmit: isValidating || !isValid,
        submitButtonText: t("archive_index_set_configuration_form.update_configuration"),
        submitLoadingText: t("archive_index_set_configuration_form.updating_configuration"),
        isSubmitting,
        displayCancel: true,
        onCancel: handleCancel
      }
    ))))
  )));
};
export default RestoredIndexSetConfigurationForm;
